/**
 * AOI 生产管理 - 共享常量 (颜色 / API key / 默认 zoom 等)。
 *
 * <p>API key 跟其他地图工具页面保持一致 (硬编码暂时方案, 后续可走环境变量 / 后端拉取)。
 */

/** Google Maps API key (跟 AddressLocationReviewDrawer.tsx 一致, 共享同一个 key)。 */
export const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';

/** Mapbox access token (跟 AddressLocationReviewDrawer.tsx 一致)。 */
export const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoibGl4aWFvaHVpdGl0YSIsImEiOiJjbWNsc2J3aTcwZDkyMmtwdGxiZW95YWt6In0.qoTeK6P5iQ8Mq62KdImwBQ';

/** Mapbox style URL: 使用 OpenStreetMap 风格的 mapbox/streets-v12 (用于其他页面, 比如 location-correction)。 */
export const MAPBOX_STYLE_URL = 'mapbox://styles/mapbox/streets-v12';

/**
 * AOI 详情页 Mapbox style - 跟 location-correction 用同一个 mapbox 官方 style.
 *
 * <p>变量名保留 `MAPBOX_OSM_STYLE` 仅为不影响导入语句; 实际是 Mapbox 官方 vector style.
 */
export const MAPBOX_OSM_STYLE: string = 'mapbox://styles/mapbox/streets-v12';

/** 地图默认 zoom (selfGeometry 缺失 / 没 bbox 时兜底)。 */
export const DEFAULT_DETAIL_ZOOM = 17;

/**
 * fitBounds 时的内边距 (px) - 让 AOI 不会贴到屏幕边缘。
 * 数字越大, 显得 AOI 周围 "留白" 越多, 视野越远; 越小则 AOI 在屏幕上越大。
 */
export const FIT_BOUNDS_PADDING_PX = 40;

/**
 * fitBounds 时的最大 zoom - 防止特别小的 AOI (e.g. 一栋小房子) 被缩到街道级,
 * 反而看不清周边环境。19 ≈ 楼栋级别。
 */
export const FIT_BOUNDS_MAX_ZOOM = 19;

/**
 * AOI 详情图层颜色 (前后端保持一致, 修改时同步 hover 图例)。
 *
 * <p>配色原则:
 * <ul>
 *   <li>3 个 polygon 颜色 (self/child/nearby) 拉开色相 (紫/青/蓝), 避免在同一张图里看不出区别;</li>
 *   <li>self 最实 (高透明度 + 粗描边), nearby 次之 (中透明度), child 居中;</li>
 *   <li>marker 颜色 (entryPoint/parkingPoint/relatedAddress) 避开 polygon 色相, 用绿 / 橙 / 红。</li>
 * </ul>
 */
export const AOI_DETAIL_COLORS = {
    /** 当前 AOI 自身 - 紫色高亮 */
    self: '#8b5cf6',
    /** 当前 AOI 描边 - 比填充更深的紫, 强化轮廓感 */
    selfStroke: '#6d28d9',
    /** 子 AOI (2级时展示) - 青色 */
    child: '#06b6d4',
    /** 周边 AOI - 天蓝 (sky-400, 比之前 sky-300 深一档, 有明显色相但仍不抢戏) */
    nearby: '#38bdf8',
    /** 进门点 - 绿色 marker */
    entryPoint: '#10b981',
    /** 停车点 - 橙色 marker */
    parkingPoint: '#f59e0b',
    /** 关联地址 - 红色 marker */
    relatedAddress: '#ef4444',
} as const;

/**
 * AOI 本体来源(biz_aoi.data_source)→ 展示标签。
 * OVERTURE / OVERTURE_AGGREGATED 统一显示 "OT"; 未知来源兜底显示原值。
 */
export const AOI_SOURCE_LABELS: Record<string, string> = {
    SURVEY: '实采',
    OVERTURE: 'OT',
    OVERTURE_AGGREGATED: 'OT',
    OSM: 'OSM',
};

/** 来源值 → 展示标签; 空="-", 未知=原值。 */
export const aoiSourceLabel = (ds: string | null | undefined): string =>
    ds == null || ds === '' ? '-' : (AOI_SOURCE_LABELS[ds] ?? ds);

/** 地图底图供应商枚举。 */
export type MapProvider = 'google' | 'mapbox';

/** 详情页图层可见性 (受控状态, 由右侧信息面板的勾选框 toggle)。 */
export interface LayerVisibility {
    showEntryPoints: boolean;
    showParkingPoints: boolean;
    /** 地址级点位图层 (关联地址各自的进门/停车点, 空心圈样式; 与楼级实心点是两层数据)。 */
    showAddressPoints: boolean;
    showRelatedAddresses: boolean;
    showChildren: boolean;
    showNearby: boolean;
}

/** 默认全显示; 地址级点位大多与楼级点位坐标重合, 默认关, 需要看两层差异时手动勾开。 */
export const DEFAULT_LAYER_VISIBILITY: LayerVisibility = {
    showEntryPoints: true,
    showParkingPoints: true,
    showAddressPoints: false,
    showRelatedAddresses: true,
    showChildren: true,
    showNearby: true,
};

/**
 * 编辑模式下「拾取点位」的目标类型。
 * <p>同一时刻只允许选择一个 target, 防止用户在编辑进门 / 停车时混淆地图点击意图。
 */
export type PickTarget = 'entryPoints' | 'parkingPoints' | null;

/** 用户在地图上点出的临时坐标 (尚未确认加入列表)。 */
export interface TempPickedPoint {
    lng: number;
    lat: number;
}

/**
 * 列表型字段的统一上限 (进门点 / 停车点 / 配送时间各分类的 ranges / other 列表 / 等)。
 *
 * <p>跟后端保持一致 (`swiftx-map-api` / `api-server` 都用同一个值)。
 * 调整时必须三端同步。
 */
export const MAX_LIST_SIZE = 50;
